import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CFormLabel,
  CFormTextarea,
  CListGroup,
  CListGroupItem,
  CSpinner,
  CStepper,
} from "@coreui/react-pro";
import {
  deriveSubscriptionDisplayStatus,
  isStaffManaged,
  useEndSubscription,
  useMySubscriptions,
} from "../../../api/hooks/useStrategySubscriptions";
import type { ApiError } from "../../../api/client";
import { useFailure } from "../../../context/useFailure";
import { useCancellationReasons, useReferenceLabel } from "../../../api/hooks/useReference";
import { useToast } from "../../../context/useToast";
import { routePaths } from "../../../routes/route-paths";
import { StrategyPriceText } from "../components/StrategyPrice";
import { formatDateShort } from "../../../lib/dates";
import { StrategySideLabel } from "../components/StrategySideLabel";

// CStepper's exposed imperative API. Mirrors the shape used by the Signal
// Manager's wizards so the patterns stay aligned.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

export function SubscriptionEndWizardCoreui() {
  const navigate = useNavigate();
  const { strategyId = "" } = useParams<{ strategyId: string }>();

  const { data: subscriptions = [], isLoading, error } = useMySubscriptions();
  const endRemote = useEndSubscription();
  const { showSuccess } = useToast();
  const { showFailure, clearFailure } = useFailure();

  const stepperRef = useRef<CStepperRef>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  // Null means not chosen yet, never "declined" - declining is its own reason. Nothing is
  // pre-selected, so the member has to answer, and answering can be "I would rather not say".
  const [reason, setReason] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [reasonValidated, setReasonValidated] = useState(false);

  const reasons = useCancellationReasons();
  const referenceLabel = useReferenceLabel();

  const chosen = (reasons.data ?? []).find((value) => value.id === reason);
  // Matched on the stable member name rather than the number, which is the reference list's contract.
  const commentRequired = chosen?.name === "Other";
  const whyComplete = chosen !== undefined && (!commentRequired || comment.trim().length > 0);

  const handleWhyNext = () => {
    if (!whyComplete) {
      setReasonValidated(true);
      return;
    }
    setReasonValidated(false);
    stepperRef.current?.next();
  };

  // Only a subscription that has not already been cancelled can be ended here. An already-cancelled
  // one would otherwise be re-ended, resetting its access-until date to today and forfeiting the
  // days the customer had already paid for — they should Resume it instead.
  const subscription = subscriptions.find((s) => {
    if (s.strategy.id !== strategyId) {
      return false;
    }
    // Staff-managed subscriptions can't be ended by the customer — guarded here too, not just by
    // hiding the button, so a direct URL can't reach this flow.
    if (isStaffManaged(s)) {
      return false;
    }
    const displayStatus = deriveSubscriptionDisplayStatus(s);
    return displayStatus === "Active";
  });

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <CAlert color="danger">
          Unable to load your subscription. Please try again.
        </CAlert>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div>
        <CAlert color="warning">
          No active subscription found for this strategy.{" "}
          <Link to={routePaths.myStrategies}>Back to my strategies</Link>.
        </CAlert>
      </div>
    );
  }

  // Cancel on the first step takes the customer back to the subscription
  // detail page they came from — not the list — so the journey feels like a
  // detour and a return rather than a teleport.
  const detailPath = `/subscriptions/${strategyId}`;

  const handleNext = () => stepperRef.current?.next();

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(detailPath);
    } else {
      stepperRef.current?.prev();
    }
  };

  // The submit step ends the subscription, surfaces a toast, and routes back
  // to the subscription detail page where the customer will see the
  // Cancelled status badge and active-to date.
  const handleSubmit = async () => {
    setSubmitting(true);
    clearFailure();
    try {
      // Guarded by the Why step: Review is unreachable without a reason.
      await endRemote.mutateAsync({ strategyId, reason: reason!, comment });
      showSuccess(`Subscription to ${subscription.strategy.publicName} ended.`);
      navigate(detailPath);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Unable to end this subscription. Please try again.";
      showFailure(message, (err as ApiError)?.traceId);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    {
      label: "Details",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Details</h4>
          </CCardHeader>
          <CCardBody>
            <p className="text-body-secondary">
              You're about to end the following subscription. Review the
              details, then continue to confirm.
            </p>
            <dl className="row mb-4">
              <dt className="col-bo-3 text-body-secondary fw-normal">
                Strategy
              </dt>
              <dd className="col-bo-9 fw-semibold">
                {subscription.strategy.publicName}
                <div className="small fw-normal mt-1">
                  <StrategySideLabel direction={subscription.strategy.direction} />
                </div>
              </dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">Status</dt>
              <dd className="col-bo-9">
                {deriveSubscriptionDisplayStatus(subscription)}
              </dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">
                Active since
              </dt>
              <dd className="col-bo-9">
                {formatDateShort(subscription.activeFrom)}
              </dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">
                Billing
              </dt>
              <dd className="col-bo-9">
                <StrategyPriceText
                  free={subscription.priceAtSubscriptionUsd === 0}
                  amountUsd={subscription.priceAtSubscriptionUsd}
                  cycle={subscription.billingCycle}
                />
              </dd>
            </dl>
            <hr className="mt-0 mb-4" />
            <div className="d-flex justify-content-between">
              <CButton color="secondary" variant="outline" onClick={handleBack}>
                Cancel
              </CButton>
              <CButton color="primary" onClick={handleNext}>
                Next: Why
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Why",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Why are you leaving?</h4>
          </CCardHeader>
          <CCardBody>
            <p className="text-body-secondary">
              It helps us fix what is worth fixing. If you would rather not, say
              so and we will leave it there.
            </p>
            <fieldset className="mb-4">
              <legend className="visually-hidden">Reason for leaving</legend>
              {/* Rows rather than bare radios: the whole row is the target, and the label wraps the
                  input so no htmlFor wiring is needed. */}
              <CListGroup>
                {(reasons.data ?? []).map((value) => (
                  <CListGroupItem
                    key={value.id}
                    as="label"
                    active={reason === value.id}
                    className="d-flex align-items-center gap-3"
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="radio"
                      className="form-check-input flex-shrink-0 m-0"
                      name="cancellation-reason"
                      checked={reason === value.id}
                      onChange={() => setReason(value.id)}
                    />
                    <span>{referenceLabel(value)}</span>
                  </CListGroupItem>
                ))}
              </CListGroup>
              {reasonValidated && chosen === undefined && (
                <div className="text-danger small mt-2">
                  Please choose one so we know where to look.
                </div>
              )}
            </fieldset>
            <CFormLabel htmlFor="cancellation-comment">
              {commentRequired
                ? "What was the reason?"
                : "Anything you would like to add?"}
            </CFormLabel>
            <CFormTextarea
              id="cancellation-comment"
              rows={6}
              maxLength={2000}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={
                commentRequired
                  ? "Tell us what we are missing from the list."
                  : "Tell us more if you would like to."
              }
              invalid={reasonValidated && commentRequired && comment.trim().length === 0}
              feedbackInvalid="Please tell us a little about why you are leaving."
            />
            <div className="d-flex justify-content-between mt-4">
              <CButton color="secondary" variant="outline" onClick={handleBack}>
                Back
              </CButton>
              <CButton color="primary" onClick={handleWhyNext}>
                Next: Review
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Review",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Review</h4>
          </CCardHeader>
          <CCardBody>
            <CAlert color="warning">
              Your access to <strong>{subscription.strategy.publicName}</strong> will
              end once the period you have already paid for runs out.
            </CAlert>
            <p>When you confirm:</p>
            <ul className="mb-4">
              <li>
                Your subscription is marked as <strong>Cancelled</strong>, and
                your access to the strategy runs to the end of the period you
                have already paid for — not to today.
              </li>
              <li>Billing stops. You will not be charged for it again.</li>
              {subscription.pendingSwitch && (
                <li>
                  Your upgrade to yearly on{" "}
                  <strong>{formatDateShort(subscription.pendingSwitch.switchDate)}</strong>{" "}
                  is cancelled too, so nothing is taken then.
                </li>
              )}
              <li>
                You can resume any time before your access ends. After that, you
                can subscribe again from the strategy list.
              </li>
            </ul>
            {/* Separated from the consequences above, which otherwise run straight into it and read as
                one dense block. Same list as the profile wizard's review - it is the pattern for
                showing someone what they are about to submit. */}
            <hr className="mt-0 mb-4" />
            <CListGroup className="mb-4">
              <CListGroupItem className="d-flex justify-content-between">
                <strong>Reason</strong>
                <span>{chosen ? referenceLabel(chosen) : "—"}</span>
              </CListGroupItem>
              {comment.trim().length > 0 && (
                // Stacked rather than inline: free text right-aligned against the label wraps into a
                // ragged column, which is what the short values above never do.
                <CListGroupItem>
                  <strong className="d-block mb-1">Your comment</strong>
                  <span
                    className="text-body-secondary"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {comment.trim()}
                  </span>
                </CListGroupItem>
              )}
            </CListGroup>
            <div className="d-flex justify-content-between">
              <CButton
                color="secondary"
                variant="outline"
                onClick={handleBack}
                disabled={submitting}
              >
                Back
              </CButton>
              <CButton
                color="danger"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting && <CSpinner size="sm" className="me-2" />}
                End subscription
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      <h2>End subscription</h2>
      <p className="text-body-secondary">
        End your active subscription to{" "}
        <strong>{subscription.strategy.publicName}</strong>.
      </p>
      <hr />

      <CStepper
        ref={stepperRef}
        steps={steps}
        onStepChange={setCurrentStep}
        onReset={() => setCurrentStep(1)}
      />
    </>
  );
}
